import { useTranslation } from 'react-i18next'; // Hook for language translation (i18n)
import { useDispatch, useSelector } from 'react-redux'; // Redux hooks for dispatching actions and accessing state
import { useNavigate } from 'react-router-dom'; // Hook for programmatic navigation
import { useEffect, useState } from 'react'; // React hooks for state and side effects
import { errorMsg, isEmailFormat, syncI18nLanguageFromBackend } from '../../../Apis/functions'; // Utility function to validate email format

// Redux async actions related to authentication
import {
    GetConfigByName,
    GetUserByIdAtEntry,
    loginMiddleware,
    loginMiddlewareV2,
    tokenApi,
} from '../../../reduxToolkit/authentication/AuthenticatonApi';

import cryptoJs from 'crypto-js'; // Library to handle encryption
import securekey from '../../../config'; // Secret key for encryption

// Common Redux actions
import { setIdSet } from '../../../reduxToolkit/Commons/CommonsSlice';
import { GetUserFullInformation } from '../../../reduxToolkit/Commons/CommonsApi';

// Redux actions for fetching provider and contractor information
import { GetProvidersByUserId } from '../../../reduxToolkit/Providers/providersApi';
import { GetContractorsByUserId } from '../../../reduxToolkit/Contractor/ContractorApi';

// Constants and models
import { SYSTEM_CONFIG_LIST } from '../../Dashboard/Setting/Model/SettingConfigModel';
import { BACK_TO_UI_LANG, LOGIN_MODEL } from '../Model/HomeModel';
import { ErrorCode } from '../../../Apis/ErrorCode';

import { toast } from 'react-toastify';
import routes from '../../../Routes/routes';
import { GetUserPreferencesByUserId } from '../../../reduxToolkit/UserPreferences/UserPreferencesApi';
import i18next from 'i18next'; // Library for displaying toast notifications

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

// Main custom hook for handling login and user setup
const HomeApp = () => {
    const { t } = useTranslation(); // i18n translation hook
    const dispatch = useDispatch(); // Redux dispatch function
    const navigate = useNavigate(); // React Router navigation function
    const params = new URLSearchParams(); // For appending query parameters
    const { idSet } = useSelector((state) => state.CommonsSlice); // Retrieve stored user ID
    const { getConfigByName } = useSelector((state) => state.SystemConfigSlice); // Retrieve config data from Redux

    // Local component state
    const [isValidOtp, setIsValidOtp] = useState(true);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [otp, setOtp] = useState('');
    const [emptyFields, setEmptyFields] = useState(false);
    const [isValidEmail, setIsValidEmail] = useState(true);
    const [loading, setLoading] = useState(false);
    const [showOtp, setShowOtp] = useState(false); // Whether OTP input should be shown

    useEffect(() => {
        // Fetch system config to determine if OTP is required
        dispatch(GetConfigByName(SYSTEM_CONFIG_LIST.enableOtp)).then(
            ({
                payload: {
                    data: { data },
                },
            }) => {
                setShowOtp(data);
            },
        );
    }, []);

    // Trigger login when Enter key is pressed
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleLogin();
        }
    };

    // Validate email format on input change
    const handleEmailChange = (e) => {
        const value = e.target.value;
        setIsValidEmail(isEmailFormat(value));
    };

    // Main login handler
    const handleLogin = () => {
        params.append('email', email);
        params.append('password', password);
        params.append('code', otp);

        // Check required fields
        if (email !== '' && isValidEmail && password !== '' && (!showOtp || (otp !== '' && showOtp))) {
            setIsValidOtp(true);
            setLoading(true);

            // First step: obtain bearer token
            dispatch(tokenApi(params)).then((resp) => {
                if (resp?.payload?.status === 200) {
                    let token = resp?.payload?.data?.access_token;
                    const accessToken = cryptoJs.AES.encrypt(token, securekey);
                    sessionStorage.setItem('bearerToken', accessToken);

                    let body = {
                        values: { ...LOGIN_MODEL.toRegister({ email, password, otp }) },
                    };

                    // Second step: login using middleware (OTP or standard)
                    dispatch(showOtp ? loginMiddleware(body) : loginMiddlewareV2(body)).then((resp) => {
                        let errorCode = resp?.payload?.code;

                        if (resp?.payload?.success) {
                            const data = resp.payload.data;

                            setIsValidOtp(true);
                            sessionStorage.setItem('id', data?.id);
                            dispatch(setIdSet(data.id));

                            const uuid = { userId: data?.id };
                            const userId = idSet || sessionStorage.getItem('id');
                            // Fetch language for user after login
                            dispatch(GetUserPreferencesByUserId(userId)).then(({ payload }) => {
                                syncI18nLanguageFromBackend(payload?.data?.data?.language);
                            });
                            // Fetch full user details after successful login
                            dispatch(GetUserFullInformation(userId)).then(({ payload: { data } }) => {
                                sessionStorage.setItem('selfie', data?.data?.selfie);
                            });

                            // Fetch role-specific details
                            if (data?.userType?.name === 'SUPPLIER_IN_CHARGE') {
                                dispatch(GetProvidersByUserId(uuid)).then(({ payload: { data } }) => {
                                    sessionStorage.setItem('providerId', data?.data?.id);
                                });
                            }

                            if (data?.userType?.name === 'CONTRACTOR_IN_CHARGE') {
                                dispatch(GetContractorsByUserId(uuid)).then(({ payload: { data } }) => {
                                    sessionStorage.setItem('contractorId', data?.data?.id);
                                });
                            }

                            if (data?.userType?.name === 'EMPLOYEE') {
                                dispatch(GetUserByIdAtEntry());
                            }

                            setIsValidOtp(true); // Reset OTP validity
                            setLoading(false); // Reset loading state
                            navigate(routes.login);
                            return;
                        }
                        if (errorCode === ErrorCode.INVALID_CODE) {
                            setIsValidOtp(false);
                        } else if (errorCode) {
                            errorMsg(errorCode);
                        }

                        // Clean session storage
                        sessionStorage.clear();

                        setLoading(false);
                    });
                    return;
                } else if (resp?.payload?.data?.status === 'BAD_REQUEST') {
                    toast.error(t('bad_credentials'));
                }

                // If token request fails, reset loading state
                setLoading(false);
            });
            return;
        }

        // Set state to show an error if any required fields are empty
        setEmptyFields(true);
    };

    const handleForgotPassword = () => {
        navigate(routes.forgotPassword)
    };

    return {
        t, // Translation function for internationalization
        emptyFields, // State for empty fields validation
        setEmptyFields, // Function to update empty fields state
        email, // State for email input
        setEmail, // Function to update email state
        password, // State for password input
        setPassword, // Function to update password state
        loading, // State for loading status
        isValidEmail, // State for email format validity
        isValidOtp, // State for OTP validity
        setIsValidOtp, // Function to update OTP validity state
        otp, // State for OTP input
        setOtp, // Function to update OTP state
        showOtp,
        getConfigByName,
        handleEmailChange, // Function to handle email input changes
        handleKeyDown, // Function to handle keydown events (Enter key)
        handleLogin, // Function to handle login logic
        handleForgotPassword,
    };
};

export default HomeApp; // Exporting HomeApp component as default
